import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import {
  type APIResponse,
  type DirectoryUser,
  type SettingsUser,
  type User,
} from "@/lib/types";

/** The API allows empty identity/contact fields; normalize these at the request boundary. */
type UserApiRow<T> = Omit<
  T,
  "firstName" | "lastName" | "email" | "phoneNumber"
> & {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
};

export type UserPage<T> = Omit<APIResponse<T[]>, "pagination"> & {
  pagination: { limit: number; offset: number; total: number };
};

export const isEnabledDirectoryUser = (user: DirectoryUser) =>
  user.enabled !== false;

export type UserDirectoryParams = {
  limit?: number;
  offset?: number;
  search?: string;
  ids?: string[];
};

export type SettingsUsersParams = Omit<UserDirectoryParams, "ids"> & {
  sortBy?:
    | "firstName"
    | "middleName"
    | "email"
    | "phoneNumber"
    | "permissionGroups"
    | "enabled"
    | "owner";
  sortDirection?: "asc" | "desc";
};

export const getUserDirectory = (
  params: UserDirectoryParams = {},
  signal?: AbortSignal,
) =>
  authenticatedFastApiAxiosInstance
    .get<UserPage<UserApiRow<DirectoryUser>>>("firms/v3/users", {
      params,
      paramsSerializer: { indexes: null },
      signal,
    })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((user) => ({
        ...user,
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        email: user.email ?? "",
        phoneNumber: user.phoneNumber ?? "",
      })),
    }));

export const getSettingsUsers = (
  params: SettingsUsersParams,
  signal?: AbortSignal,
) =>
  authenticatedFastApiAxiosInstance
    .get<
      UserPage<UserApiRow<SettingsUser>>
    >("firms/v3/users/settings", { params, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((user) => ({
        ...user,
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        email: user.email ?? "",
        phoneNumber: user.phoneNumber ?? "",
      })),
    }));

/** Complete lean directory for non-paginated identity/mention lookups. Selectors use getUserDirectory. */
export const getAllDirectoryUsers = async (
  signal?: AbortSignal,
): Promise<APIResponse<DirectoryUser[]>> => {
  const first = await getUserDirectory({ limit: 500 }, signal);
  const users = [...first.data];
  let page = first;
  while (
    page.pagination.offset + page.data.length < page.pagination.total &&
    page.data.length > 0
  ) {
    page = await getUserDirectory(
      { limit: 500, offset: page.pagination.offset + page.data.length },
      signal,
    );
    users.push(...page.data);
  }
  return {
    data: [...new Map(users.map((user) => [user.id, user])).values()],
    meta: first.meta,
  };
};

export const getCurrentUserV1 = () => {
  return authenticatedFastApiAxiosInstance
    .get<APIResponse<User>>("firms/v2/users/current")
    .then((res) => res.data);
};

export const createUserV1 = (
  data: Pick<
    User,
    "email" | "firstName" | "lastName" | "phoneNumber" | "permissionGroupIds"
  > &
    Partial<User>,
) => {
  return authenticatedFastApiAxiosInstance
    .post<APIResponse<User>>(`firms/v2/users`, data)
    .then((res) => res.data);
};

export const updateUserV1 = (
  data: Pick<User, "id" | "dateModified"> & Partial<User>,
) => {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<User>>(`firms/v2/users`, data)
    .then((res) => res.data);
};

export const disableUserV1 = (id: string) => {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<User>>(`firms/v2/users/${id}/disable`)
    .then((res) => res.data);
};

export const enableUserV1 = (id: string) => {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<User>>(`firms/v2/users/${id}/enable`)
    .then((res) => res.data);
};

export const resetUserMfaV1 = (id: string) => {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<unknown>>(`firms/v2/users/reset-mfa`, {
      id,
    })
    .then((res) => res.data);
};

export const deleteUserV1 = (id: string) => {
  return authenticatedFastApiAxiosInstance
    .delete<APIResponse<User>>(`firms/v2/users/${id}`)
    .then((res) => res.data);
};
